/**
 * ChatWindowManager - Manages multiple conversation windows
 *
 * DATE: 2025-12-19 | Author: IA + Luiz Couto
 *
 * Singleton service that manages ConversationWindow instances,
 * handles positioning, persistence, and mobile detection.
 *
 * Part of the Multi-Window Chat System refactoring.
 */

import { ConversationWindow, WindowPosition, ConversationWindowConfig } from '../components/ConversationWindow';

// ============================================================================
// TYPES
// ============================================================================

interface WindowState {
  friendId: number;
  friendNickname: string;
  isAI: boolean;
  position: WindowPosition;
  isMinimized: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Window dimensions for positioning
const WINDOW_WIDTH = 320;
const WINDOW_HEIGHT = 420;
const WINDOW_GAP = 10;
const WINDOW_BOTTOM_OFFSET = 10;

// DATE: 2025-12-20 | Author: IA + Luiz Couto
// Space reserved for game chat sidebar on the right (when in game room)
const GAME_CHAT_SIDEBAR_WIDTH = 340; // 320px sidebar + 20px margin
// Space reserved for FriendsHub on the right
const FRIENDS_HUB_WIDTH = 340; // 320px hub + 20px margin

// Mobile breakpoint
const MOBILE_BREAKPOINT = 768;

// LocalStorage keys
const STORAGE_KEY_WINDOWS = 'domino_chat_open_windows';

// ============================================================================
// CHAT WINDOW MANAGER SINGLETON
// ============================================================================

export class ChatWindowManager {
  // Singleton instance
  private static instance: ChatWindowManager | null = null;

  // Map of friendId -> ConversationWindow
  private windows: Map<number, ConversationWindow> = new Map();

  // DATE: 2025-12-20 | Author: IA + Luiz Couto
  // Z-index counter for stacking windows
  // Must be higher than: FriendsHub (9000), game chat sidebar, other UI elements
  private zIndexCounter: number = 10000;

  // Callback for when mobile mode and window should open in single-panel
  private onMobileOpenCallback: ((friendId: number, nickname: string, isAI: boolean) => void) | null = null;

  // ============================================================================
  // SINGLETON PATTERN
  // ============================================================================

  private constructor() {
    console.log(`💬 [2025-12-19] ChatWindowManager: Singleton instance created`);

    // Listen for window resize to handle mobile/desktop transitions
    window.addEventListener('resize', () => {
      this.handleResize();
    });
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): ChatWindowManager {
    if (!ChatWindowManager.instance) {
      ChatWindowManager.instance = new ChatWindowManager();
    }
    return ChatWindowManager.instance;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Open a conversation window for a friend
   * Returns the window instance, or null if in mobile mode
   */
  public openWindow(friendId: number, nickname: string, isAI: boolean): ConversationWindow | null {
    console.log(`💬 [2025-12-19] ChatWindowManager: Opening window for ${nickname} (${friendId})`);

    // Check if in mobile mode
    if (this.isMobileMode()) {
      console.log(`📱 [2025-12-19] ChatWindowManager: Mobile mode - delegating to single-panel`);
      if (this.onMobileOpenCallback) {
        this.onMobileOpenCallback(friendId, nickname, isAI);
      }
      return null;
    }

    // Check if window already exists
    const existingWindow = this.windows.get(friendId);
    if (existingWindow) {
      console.log(`💬 [2025-12-19] ChatWindowManager: Window already exists - focusing`);
      existingWindow.focus();
      return existingWindow;
    }

    // Calculate position for new window
    const position = this.calculateNextPosition();

    // Create new window
    const config: ConversationWindowConfig = {
      friendId,
      friendNickname: nickname,
      isAI,
      position
    };

    const window = new ConversationWindow(config);

    // Setup callbacks
    window.onClose((id) => this.closeWindow(id));
    window.onFocus((id) => this.handleWindowFocus(id));

    // Store window
    this.windows.set(friendId, window);

    // Render window
    window.render();

    // Focus the new window
    window.focus();

    // Save state to localStorage
    this.saveState();

    return window;
  }

  /**
   * Close a conversation window
   */
  public closeWindow(friendId: number): void {
    console.log(`💬 [2025-12-19] ChatWindowManager: Closing window for ${friendId}`);

    const window = this.windows.get(friendId);
    if (window) {
      window.destroy();
      this.windows.delete(friendId);
      this.saveState();
    }
  }

  /**
   * Close all windows
   */
  public closeAllWindows(): void {
    console.log(`💬 [2025-12-19] ChatWindowManager: Closing all windows`);

    this.windows.forEach((chatWindow) => {
      chatWindow.destroy();
    });
    this.windows.clear();
    this.saveState();
  }

  /**
   * Get a specific window
   */
  public getWindow(friendId: number): ConversationWindow | null {
    return this.windows.get(friendId) || null;
  }

  /**
   * Get all open windows
   */
  public getOpenWindows(): ConversationWindow[] {
    return Array.from(this.windows.values());
  }

  /**
   * Get count of open windows
   */
  public getWindowCount(): number {
    return this.windows.size;
  }

  /**
   * Check if we're in mobile mode
   */
  public isMobileMode(): boolean {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /**
   * Set callback for when a window should open in mobile mode
   * This delegates to the FriendsHub (PrivateChat) single-panel mode
   */
  public setMobileOpenCallback(callback: (friendId: number, nickname: string, isAI: boolean) => void): void {
    this.onMobileOpenCallback = callback;
  }

  /**
   * Save current windows state to localStorage
   */
  public saveState(): void {
    try {
      const state: WindowState[] = [];

      this.windows.forEach((window, friendId) => {
        const position = window.getPosition();
        state.push({
          friendId,
          friendNickname: '', // Will be populated from ChatService on restore
          isAI: false, // Will be populated from ChatService on restore
          position,
          isMinimized: window.isWindowMinimized()
        });
      });

      localStorage.setItem(STORAGE_KEY_WINDOWS, JSON.stringify(state));
      console.log(`💾 [2025-12-19] ChatWindowManager: Saved state - ${state.length} windows`);
    } catch (error) {
      console.warn(`⚠️ [2025-12-19] ChatWindowManager: Could not save state:`, error);
    }
  }

  /**
   * Restore windows from localStorage
   * Should be called after ChatService is connected
   */
  public async restoreState(): Promise<void> {
    // Don't restore in mobile mode
    if (this.isMobileMode()) {
      console.log(`📱 [2025-12-19] ChatWindowManager: Mobile mode - not restoring windows`);
      return;
    }

    try {
      const stateJson = localStorage.getItem(STORAGE_KEY_WINDOWS);
      if (!stateJson) {
        console.log(`💬 [2025-12-19] ChatWindowManager: No saved state to restore`);
        return;
      }

      const state: WindowState[] = JSON.parse(stateJson);
      console.log(`💬 [2025-12-19] ChatWindowManager: Restoring ${state.length} windows`);

      // Import ChatService to get friend info
      const { ChatService } = await import('./ChatService');
      const chatService = ChatService.getInstance();

      for (const windowState of state) {
        // Get friend info from ChatService
        const friend = chatService.getFriend(windowState.friendId);
        if (!friend) {
          console.warn(`⚠️ [2025-12-19] ChatWindowManager: Friend ${windowState.friendId} not found - skipping`);
          continue;
        }

        // Open window with saved position
        const config: ConversationWindowConfig = {
          friendId: windowState.friendId,
          friendNickname: friend.nickname,
          isAI: friend.isAI,
          position: windowState.position
        };

        const window = new ConversationWindow(config);
        window.onClose((id) => this.closeWindow(id));
        window.onFocus((id) => this.handleWindowFocus(id));

        this.windows.set(windowState.friendId, window);
        window.render();

        // Restore minimized state
        if (windowState.isMinimized) {
          window.minimize();
        }
      }

    } catch (error) {
      console.warn(`⚠️ [2025-12-19] ChatWindowManager: Could not restore state:`, error);
    }
  }

  /**
   * Clear saved state
   */
  public clearSavedState(): void {
    try {
      localStorage.removeItem(STORAGE_KEY_WINDOWS);
      console.log(`💾 [2025-12-19] ChatWindowManager: Cleared saved state`);
    } catch (error) {
      console.warn(`⚠️ [2025-12-19] ChatWindowManager: Could not clear state:`, error);
    }
  }

  // ============================================================================
  // PRIVATE - POSITIONING
  // ============================================================================

  /**
   * Calculate position for next window
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * DATE: 2025-12-26 | Updated: Position windows at BOTTOM-LEFT corner
   * This keeps private chats separate from game chat (right side)
   * - Starts from bottom-left corner
   * - Cascades horizontally for multiple windows
   * - Wraps to new row if needed
   */
  private calculateNextPosition(): WindowPosition {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Reserve space on the right for game chat sidebar and FriendsHub
    const rightReserved = Math.max(GAME_CHAT_SIDEBAR_WIDTH, FRIENDS_HUB_WIDTH);

    // Calculate how many windows can fit horizontally
    const existingCount = this.windows.size;

    // DATE: 2025-12-26 | Start from LEFT edge (bottom-left corner)
    // Cascade horizontally: each new window appears to the right of the previous
    const horizontalCascade = existingCount * (WINDOW_WIDTH + WINDOW_GAP);
    let x = WINDOW_GAP + horizontalCascade;

    // Maximum X position (leave space for game chat on right)
    const maxX = viewportWidth - rightReserved - WINDOW_WIDTH - WINDOW_GAP;

    // If window would go past max, wrap to next row
    let row = 0;
    if (x > maxX) {
      // Calculate how many windows fit per row
      const windowsPerRow = Math.max(1, Math.floor((maxX - WINDOW_GAP) / (WINDOW_WIDTH + WINDOW_GAP)));
      row = Math.floor(existingCount / windowsPerRow);
      const col = existingCount % windowsPerRow;
      x = WINDOW_GAP + col * (WINDOW_WIDTH + WINDOW_GAP);
    }

    // Boundary checks
    x = Math.max(WINDOW_GAP, x);
    x = Math.min(x, maxX);

    // Calculate Y position (from bottom)
    // Each row goes higher on the screen
    const rowOffset = row * (WINDOW_HEIGHT + WINDOW_GAP);
    let y = WINDOW_BOTTOM_OFFSET + rowOffset;

    // Boundary check: ensure window doesn't go off the top
    const maxY = viewportHeight - WINDOW_HEIGHT - WINDOW_GAP;
    y = Math.min(y, maxY);
    y = Math.max(WINDOW_BOTTOM_OFFSET, y);

    console.log(`📍 [2025-12-26] ChatWindowManager: Position BOTTOM-LEFT x=${x}, y=${y} (window ${existingCount + 1}, row ${row})`);

    return { x, y };
  }

  // ============================================================================
  // PRIVATE - EVENT HANDLERS
  // ============================================================================

  /**
   * Handle window focus (bring to front)
   */
  private handleWindowFocus(friendId: number): void {
    // Increment z-index for focused window
    this.zIndexCounter++;

    const window = this.windows.get(friendId);
    if (window) {
      const container = document.getElementById(`chat-window-${friendId}`);
      if (container) {
        container.style.zIndex = String(this.zIndexCounter);
      }
    }
  }

  /**
   * Handle window resize
   * Close all windows if transitioning to mobile
   */
  private handleResize(): void {
    if (this.isMobileMode() && this.windows.size > 0) {
      console.log(`📱 [2025-12-19] ChatWindowManager: Transitioned to mobile - closing windows`);
      // Save state first
      this.saveState();
      // Close all windows (they'll use single-panel mode)
      this.closeAllWindows();
    }
  }
}
